import React, { useEffect, useState } from "react";
import baseURL from "../../../../configs/api";
import { GridColDef } from "@mui/x-data-grid";
import AddCreditCard from "./AddCreditCard";
import DeleteCreditCard from "./DeleteCreditCard";
import { useTheme } from "../../../../components/hooks/useTheme";
import { CustomDataGrid } from "../../../../components/common/CustomDataGrid";
import colorConfigs from "../../../../configs/colorConfigs";
import Cookies from "js-cookie";
import useTenant from "../../../../components/hooks/useTenant";

type Props = {
  idNumber: string;
  customerData: any | null;
};

export default function CreditCards({ idNumber, customerData }: Props) {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { themeStyles } = useTheme();
  const { handleTenantError }: any = useTenant();
  const [fullName, setFullName] = useState<string>("");
  const [tableData, setTableData] = useState<any[]>([]);
  const [isSaved, setIsSaved] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [particularCreditDetails, setParticularCreditDetails] = useState<any>();
  const [editPopup, setEditPopup] = useState<boolean>(false);
  const [addCreditCardPopup, setAddCreditCardPopup] = useState<boolean>(false);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerClassName: themeStyles.table.headerBg,
      headerName: "#",
      align: "left",
      editable: false,
      sortable: false,
      width: 120
    },
    {
      field: "paymentGateway",
      headerName: "Payment Gateway",
      headerClassName: themeStyles.table.headerBg,
      editable: false,
      sortable: false,
      align: "left",
      flex: 170,

    },
    {
      field: "cardholderName",
      headerName: "Cardholder's Name",
      headerClassName: themeStyles.table.headerBg,
      editable: false,
      sortable: false,
      width: 220,
      align: "left",
      renderCell: (params) => {
        return (
          <span
            onClick={() => {
              setEditPopup(true);
              setParticularCreditDetails(params.row);
            }}
            style={{ color: "var(--app-link-color)" }}
            className="underline cursor-pointer"
          >
            {params?.value}
          </span>
        );
      },
    },
    {
      field: "brand",
      headerName: "Brand",
      editable: false,
      headerClassName: themeStyles.table.headerBg,
      sortable: false,
      width: 170,
      align: "left"
    },
    {
      field: "expirationDate",
      headerName: "Expiration Date",
      editable: false,
      headerClassName: themeStyles.table.headerBg,
      sortable: false,
      width: 170,
      align: "left"
    },
    {
      field: "creditCardNumber",
      headerName: "Last 4 Digits",
      editable: false,
      headerClassName: themeStyles.table.headerBg,
      sortable: false,
      width: 180,
      align: "left"
    },
  ];

  useEffect(() => {
    if (customerData) {
      setFullName(customerData?.firstName + " " + customerData?.lastName);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerData])

  useEffect(() => {
    if (isSaved) {
      setLoading(true);
      fetch(`${baseURL}creditCard?customerId=${idNumber}`, {
        method: "GET",
        headers: {
          'X-Tenant-ID': tenantId,
          "Content-Type": "application/json"
        },
      })
        .then((res) => res.json())
        .then((data) => {
          setTableData(() =>
            data.map((item: any) => {
              return {
                ...item,
                creditCardNumber: item?.creditCardNumber.toString().substr(-4)
              }
            })
          );
          setLoading(false);
          setIsSaved(false);
          handleTenantError(null);
        })
        .catch((error) => {
          setLoading(false);
          setIsSaved(false);
          handleTenantError(error);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaved]);

  return (
    <>
      <div className="w-full mt-2">
        <div className={`w-full pb-4`}>
          <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
            <div className="flex items-center gap-1.5">
              <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Customer - {fullName}</h2>
            </div>

            <div className="flex items-center gap-1.5">

              <button
                className={`px-[12px] py-[10px] ${colorConfigs.btn.main.tailwind} shadow-sm rounded-lg border ${themeStyles.main.borderColor} text-white flex items-center gap-1.5`}
                onClick={() => setAddCreditCardPopup(true)}
              >
                <span className="text-xs font-medium">Add Credit Card</span>
              </button>
            </div>
          </div>
        </div>

        <div className={`w-full border ${themeStyles.main.borderColor} rounded-[16px]`}>
          <div className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}>
            <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Credit Cards</h2>
          </div>

          <CustomDataGrid
            filteredRows={tableData}
            columns={columns}
            loading={loading}
          />
        </div>
      </div>

      <AddCreditCard
        idNumber={idNumber}
        fullName={fullName}
        addCreditCardPopup={addCreditCardPopup}
        setAddCreditCardPopup={() => {
          setAddCreditCardPopup(false);
          setIsSaved(true);
        }}
      />

      <DeleteCreditCard
        customerName={fullName}
        editPopup={editPopup}
        hidePopup={() => setEditPopup(false)}
        deleteCreditCard={() => {
          setEditPopup(false);
          setIsSaved(true);
        }}
        particularCreditDetails={particularCreditDetails}
      />
    </>
  );
};